import { useState } from 'react';
import { Card, CardHeader, Button, Badge, Input, LoadingSkeleton } from '@nexora/design-system';
import {
  useModuleCtx,
  useProductivityComparative,
  TREND_LABEL,
} from './useOkrs';
import { formatNumber } from '../../shared/utils/format';

/* ═══════════════════════════════════════════════════════════════════════════
   Comparativo de Produtividade Departamental
   (protótipo `erpone-comparativo-de-produtividade-departamental`)
   ───────────────────────────────────────────────────────────────────────────
   Contrato consumido (backend real, DTOs em okrs.dto.ts):
   - GET /okrs/productivity/comparative?departamento&periodo → ProductivityComparativeDto
       { departamentos: ScoreDepartamentoDto[], melhorDepartamento, piorDepartamento,
         mediaGeral, periodoAtual }

   Comparativo departamental com ranking: melhor/pior departamento, média geral
   e barras proporcionais ao score (CSS puro, sem lib de gráfico).
   ═══════════════════════════════════════════════════════════════════════════ */

function scorePercent(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${formatNumber(value, { maximumFractionDigits: 1 })}%`;
}

export function ProductivityComparativoPage(): JSX.Element {
  const ctx = useModuleCtx();
  const [deptFilter, setDeptFilter] = useState('');
  const { data, isLoading, error } = useProductivityComparative(ctx, {
    departamento: deptFilter || undefined,
  });

  const departamentos = [...(data?.departamentos ?? [])].sort((a, b) => b.scoreMedio - a.scoreMedio);
  const mediaGeral = data?.mediaGeral ?? 0;
  const maxScore = Math.max(100, ...departamentos.map((d) => d.scoreMedio));

  return (
    <div className="module-page">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700, color: '#191c1e' }}>Comparativo de Produtividade</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#43474e', fontSize: '0.875rem' }}>
            Comparação entre departamentos {data?.periodoAtual ? `— período ${data.periodoAtual}` : ''}.
          </p>
        </div>
        <div style={{ width: '240px' }}>
          <Input
            label="Filtrar por departamento"
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
            placeholder="Ex.: Vendas"
          />
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <>
          <div style={{ background: '#fff', border: '1px solid #e0e3e5', borderRadius: '10px', padding: '1rem' }}>
            <p style={{ margin: 0, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#74777f' }}>Média geral</p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '1.5rem', fontWeight: 700, color: '#191c1e' }}>{scorePercent(mediaGeral)}</p>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e0e3e5', borderRadius: '10px', padding: '1rem' }}>
            <p style={{ margin: 0, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#74777f' }}>Melhor departamento</p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '1.25rem', fontWeight: 700, color: '#2e7d32' }}>
              {data?.melhorDepartamento ?? '—'}
            </p>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e0e3e5', borderRadius: '10px', padding: '1rem' }}>
            <p style={{ margin: 0, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#74777f' }}>Pior departamento</p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '1.25rem', fontWeight: 700, color: '#ba1a1a' }}>
              {data?.piorDepartamento ?? '—'}
            </p>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e0e3e5', borderRadius: '10px', padding: '1rem' }}>
            <p style={{ margin: 0, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#74777f' }}>Departamentos avaliados</p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '1.5rem', fontWeight: 700, color: '#191c1e' }}>{departamentos.length}</p>
          </div>
        </>
      </div>

      <Card variant="outlined" padding="0">
        <CardHeader className="px-md pt-md">
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Eficiência por Departamento</h3>
        </CardHeader>
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
            <LoadingSkeleton />
            <span style={{ fontSize: '0.875rem' }}>Carregando comparativo...</span>
          </div>
        )}
        {error && !isLoading && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar comparativo</p>
            <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>
              {error instanceof Error ? error.message : 'Erro desconhecido'}
            </p>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()} style={{ marginTop: '1rem' }}>Tentar novamente</Button>
          </div>
        )}
        {!isLoading && !error && departamentos.length === 0 && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f', fontSize: '0.875rem' }}>
            Nenhum departamento com score registrado para comparação.
          </div>
        )}
        {!isLoading && !error && departamentos.length > 0 && (
          <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {departamentos.map((d) => {
              const widthPct = Math.max(4, Math.round((d.scoreMedio / maxScore) * 100));
              const isBest = data?.melhorDepartamento != null && d.departamento === data.melhorDepartamento;
              const isWorst = data?.piorDepartamento != null && d.departamento === data.piorDepartamento;
              return (
                <div key={d.departamento}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#191c1e' }}>
                      {d.departamento}
                      {isBest && <Badge variant="success" size="sm" className="ml-sm">Melhor</Badge>}
                      {isWorst && <Badge variant="error" size="sm" className="ml-sm">Requer atenção</Badge>}
                    </span>
                    <span style={{ fontWeight: 700, fontSize: '0.875rem', color: '#191c1e' }}>{scorePercent(d.scoreMedio)}</span>
                  </div>
                  <div style={{ height: '22px', background: '#eef0f2', borderRadius: '6px', overflow: 'hidden' }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${widthPct}%`,
                        background: isWorst ? '#ba1a1a' : '#002045',
                        borderRadius: '6px',
                        transition: 'width 0.4s ease',
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem', fontSize: '0.75rem', color: '#74777f' }}>
                    <span>
                      {d.totalOperadores} {d.totalOperadores === 1 ? 'operador' : 'operadores'} ·{' '}
                      {d.melhorOperador ? `melhor: ${d.melhorOperador}` : '—'}
                    </span>
                    <span>
                      Variação {d.variacao > 0 ? '+' : ''}{formatNumber(d.variacao, { maximumFractionDigits: 1 })} pp ·{' '}
                      {TREND_LABEL[d.tendencia]}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <style>{`.module-page { padding: 1.5rem; }`}</style>
    </div>
  );
}